import React, { useEffect, useState, useCallback } from 'react';
import { apiGet, apiPut } from '../../../lib/api';
import { toast } from 'sonner';
import { Users, UserPlus, LogIn, LogOut, DoorOpen } from 'lucide-react';
import { formatISTDateTime } from '../../../lib/format';
import { useAuth } from '../../../contexts/AuthContext';
import VisitorRegister from './VisitorRegister';
import { useUrlTab } from '../../../lib/hooks/useUrlState';

const STATUS_COLORS = {
  expected: 'bg-slate-100 text-slate-600 border-slate-300',
  checked_in: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  checked_out: 'bg-blue-50 text-blue-700 border-blue-200',
  no_show: 'bg-red-50 text-red-600 border-red-200',
};

export default function VisitorLog() {
  const { user } = useAuth();
  const [tab, setTab] = useUrlTab('tab', 'today');
  const [visitors, setVisitors] = useState([]);
  const [drawer, setDrawer] = useState(null); // 'pre' | 'walkin'

  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const canWrite = ['super_admin', 'admin', 'hr', 'md'].includes(role) || dept === 'HR' || dept === 'MD' || dept === 'ADMIN';

  const load = useCallback(() => {
    if (tab === 'onprem') {
      apiGet('/admin/visitors/active').then((r) => setVisitors(r.visitors || [])).catch(() => {});
    } else if (tab === 'today') {
      const today = new Date().toISOString().slice(0, 10);
      apiGet('/admin/visitors', { date: today }).then((r) => setVisitors(r.visitors || [])).catch(() => {});
    } else {
      apiGet('/admin/visitors').then((r) => setVisitors(r.visitors || [])).catch(() => {});
    }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const checkIn = async (v) => {
    const badge = window.prompt('Badge number (optional):', '');
    if (badge === null) return;
    try { await apiPut(`/admin/visitors/${v.id}/check-in`, { badge_no: badge || null }); toast.success('Checked in — host notified'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };
  const checkOut = async (v) => {
    try { await apiPut(`/admin/visitors/${v.id}/check-out`, {}); toast.success('Checked out'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="visitor-log-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">HR · Admin · Security</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Visitor Log</h1>
          <p className="text-sm text-slate-600 mt-1">Front-desk register · who's on premises right now.</p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <button onClick={() => setDrawer('pre')} data-testid="preregister-btn" className="px-3 py-2 border border-slate-200 rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-slate-50 flex items-center gap-1.5"><UserPlus size={14} /> Pre-register</button>
            <button onClick={() => setDrawer('walkin')} data-testid="walkin-btn" className="px-4 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] flex items-center gap-1.5"><DoorOpen size={14} /> Walk-in</button>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        {[['today', 'Today'], ['onprem', 'On Premises'], ['all', 'All']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-3 py-1.5 rounded-sm text-[11px] font-display font-bold uppercase tracking-wider border ${tab === k ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-200'}`}>{l}</button>
        ))}
      </div>

      {visitors.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="visitors-empty"><Users size={40} className="mx-auto mb-3 opacity-40" /><p className="text-sm">{tab === 'onprem' ? 'Nobody on premises right now.' : 'No visitors.'}</p></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500"><tr>
              {['Visitor', 'Meeting', 'Location', 'In', 'Out', 'Status', 'Actions'].map((h) => <th key={h} className="text-left px-3 py-2 font-display font-bold uppercase tracking-wider text-[10px]">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {visitors.map((v) => (
                <tr key={v.id} data-testid={`visitor-row-${v.id}`}>
                  <td className="px-3 py-2"><div className="font-display font-bold">{v.visitor_name}</div><div className="text-slate-400 text-[10px]">{v.company || ''}{(v.badge_no || v.badge_number) ? ` · #${v.badge_no || v.badge_number}` : ''}</div></td>
                  <td className="px-3 py-2 text-slate-600">{v.host_name || v.person_to_meet_name || '—'}<div className="text-slate-400 text-[10px]">{v.purpose || ''}</div></td>
                  <td className="px-3 py-2 text-slate-600">{v.location}</td>
                  <td className="px-3 py-2 text-slate-500">{(v.in_time || v.checked_in_at) ? formatISTDateTime(v.in_time || v.checked_in_at) : '—'}</td>
                  <td className="px-3 py-2 text-slate-500">{(v.out_time || v.checked_out_at) ? formatISTDateTime(v.out_time || v.checked_out_at) : '—'}</td>
                  <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm border ${STATUS_COLORS[v.status]}`}>{v.status.replace('_', ' ')}</span></td>
                  <td className="px-3 py-2">
                    {canWrite && (v.status === 'expected' ? (
                      <button onClick={() => checkIn(v)} data-testid={`checkin-${v.id}`} className="text-emerald-700 font-bold flex items-center gap-1"><LogIn size={12} /> Check in</button>
                    ) : v.status === 'checked_in' ? (
                      <button onClick={() => checkOut(v)} data-testid={`checkout-${v.id}`} className="text-blue-700 font-bold flex items-center gap-1"><LogOut size={12} /> Check out</button>
                    ) : <span className="text-slate-300">—</span>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawer && <VisitorRegister mode={drawer} onClose={() => setDrawer(null)} onSaved={() => { setDrawer(null); load(); }} />}
    </div>
  );
}
